/* eslint-disable @typescript-eslint/no-explicit-any */
import { Holiday } from '@treasury/domain/dates';
import { FrequencyType } from '@treasury/domain/shared';
import { BillPayFrequency } from '../src/business-bill-pay/components/bill-pay-frequency';
import { BillPayFrequencyType } from '../src/business-bill-pay/components/bill-pay-frequency.types';

//Type passed into Vaadin date picker isDisabled property function
export interface DatePickerDate {
    year: number;
    month: number;
    day: number;
}

export enum ClosestBusinessDayDirection {
    Before,
    After,
}

export interface DisableDateFunction {
    (datePickerDate: DatePickerDate, ...args: any[]): boolean;
}

export const inactiveWeekends: DisableDateFunction = (datePickerDate: DatePickerDate) => {
    const date = new Date(datePickerDate.year, datePickerDate.month, datePickerDate.day);
    return date.getDay() === 0 || date.getDay() === 6;
};

export const sameDay = (d1: Date, d2: Date) => {
    return (
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate()
    );
};

export const inactiveHolidays: DisableDateFunction = (
    datePickerDate: DatePickerDate,
    holidays: Holiday[]
) => {
    if (holidays.length === 0) {
        return false;
    }

    const date = new Date(datePickerDate.year, datePickerDate.month, datePickerDate.day);
    return holidays.some(holiday => sameDay(holiday.dateObj, date));
};

export const inactiveDates: DisableDateFunction = (
    datePickerDate: DatePickerDate,
    inactiveDates: Date[] = []
) => {
    if (inactiveDates.length === 0) {
        return false;
    }

    const date = new Date(datePickerDate.year, datePickerDate.month, datePickerDate.day);
    return inactiveDates.some(inactiveDate => sameDay(inactiveDate, date));
};

export const inactiveStartOnDates: DisableDateFunction = (
    datePickerDate: DatePickerDate,
    enabledStartOnDates: Date[]
) => {
    const date = new Date(datePickerDate.year, datePickerDate.month, datePickerDate.day);
    return !enabledStartOnDates.some(availableDate => sameDay(availableDate, date));
};

export const closestBusinessDate = (
    date: Date,
    inactiveDates: Date[] = [],
    closestBusinessDayDirection: ClosestBusinessDayDirection = ClosestBusinessDayDirection.After
) => {
    const result = new Date(date.getTime());
    const modifier = closestBusinessDayDirection === ClosestBusinessDayDirection.After ? 1 : -1;
    while (inactiveDates.some(inactiveDate => sameDay(inactiveDate, result))) {
        result.setDate(result.getDate() + modifier);
    }

    return result;
};

export const isLastDayOfMonth = (date: Date) => {
    const test = new Date(date.getTime());
    test.setDate(test.getDate() + 1);
    return test.getDate() === 1;
};

export const updateEnabledStartOnDates = (
    datePickerDate: DatePickerDate,
    inactiveDates: Date[],
    frequency: BillPayFrequency
) => {
    const date = new Date(datePickerDate.year, datePickerDate.month, datePickerDate.day);
    const { enabledStartOnDates, startOnDateRangeChecked } = frequency;

    /* If date is in range that has already been checked then skip check */
    if (
        startOnDateRangeChecked.start &&
        date.getTime() >= startOnDateRangeChecked.start.getTime() &&
        startOnDateRangeChecked.end &&
        date.getTime() <= startOnDateRangeChecked.end.getTime()
    ) {
        return;
    }

    const {
        closestBusinessDayDirection,
        type,
        repeatOnWeekday,
        repeatOnDay,
        repeatOnDay2,
        repeatOnLastBusinessDay,
    } = frequency;

    let checkDate = new Date(date.getTime());

    if (!startOnDateRangeChecked.start) {
        startOnDateRangeChecked.start = new Date();
        checkDate = new Date();
    }

    if (closestBusinessDayDirection === ClosestBusinessDayDirection.Before) {
        if (!startOnDateRangeChecked.end) {
            startOnDateRangeChecked.end = new Date();
        }
        checkDate.setMonth(checkDate.getMonth() + 2);
        checkDate.setDate(0);
    }

    while (
        closestBusinessDayDirection === ClosestBusinessDayDirection.Before
            ? checkDate.getTime() > (startOnDateRangeChecked.end?.getTime() || 0)
            : checkDate.getTime() <= date.getTime()
    ) {
        let enabledStartOnDate = false;
        switch (type) {
            case FrequencyType.Weekly:
            case FrequencyType.EveryTwoWeeks:
            case BillPayFrequencyType.EveryFourWeeks:
                if (repeatOnWeekday && checkDate.getDay() === repeatOnWeekday) {
                    enabledStartOnDate = true;
                }
                break;
            case FrequencyType.TwiceAMonth:
            case FrequencyType.Monthly:
            case BillPayFrequencyType.EveryOtherMonth:
            case FrequencyType.Quarterly:
            case BillPayFrequencyType.EveryThreeMonths:
            case FrequencyType.EverySixMonths:
            case FrequencyType.Yearly:
                if (
                    (repeatOnDay && repeatOnDay === checkDate.getDate()) ||
                    (repeatOnDay2 && repeatOnDay2 === checkDate.getDate()) ||
                    (repeatOnLastBusinessDay && isLastDayOfMonth(checkDate))
                ) {
                    enabledStartOnDate = true;
                }
                break;
            default:
                break;
        }

        if (enabledStartOnDate) {
            const businessDate = closestBusinessDate(
                checkDate,
                inactiveDates,
                closestBusinessDayDirection
            );
            const dateAlreadyExists = !!enabledStartOnDates.find(availableDate => {
                return availableDate.getTime() === businessDate.getTime();
            });
            if (!dateAlreadyExists) {
                enabledStartOnDates.push(businessDate);
            }
        }

        const modifier = closestBusinessDayDirection === ClosestBusinessDayDirection.After ? 1 : -1;
        checkDate.setDate(checkDate.getDate() + modifier);
    }

    enabledStartOnDates.sort((a, b) => a.getTime() - b.getTime());
    startOnDateRangeChecked.end = new Date(date.getTime());
};
